import { useState } from 'react';
import { Link } from 'react-router-dom';
import { estimateAPI } from '../../services/api';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '-';
    return `$${Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '-';
    return `${Number(value).toFixed(2)}%`;
};

const CONTAINER_TYPES = ['20GP', '40GP', '40HC', '45HC'];

const ORIGIN_PORTS = [
    { code: 'CNSHA', label: 'Shanghai (CNSHA)', country: 'CN' },
    { code: 'CNNGB', label: 'Ningbo (CNNGB)', country: 'CN' },
    { code: 'CNSZX', label: 'Shenzhen (CNSZX)', country: 'CN' },
    { code: 'VNSGN', label: 'Ho Chi Minh (VNSGN)', country: 'VN' },
    { code: 'VNHPH', label: 'Hai Phong (VNHPH)', country: 'VN' },
    { code: 'INMUN', label: 'Mundra (INMUN)', country: 'IN' },
    { code: 'INNSA', label: 'Nhava Sheva (INNSA)', country: 'IN' }
];

const US_PORTS = [
    { code: 'USLAX', label: 'Los Angeles (USLAX)' },
    { code: 'USLGB', label: 'Long Beach (USLGB)' },
    { code: 'USNYC', label: 'New York (USNYC)' },
    { code: 'USSAV', label: 'Savannah (USSAV)' },
    { code: 'USHOU', label: 'Houston (USHOU)' }
];

const PRODUCT_CATEGORIES = [
    { value: '', label: 'None (skip duties)' },
    { value: 'ceramic_tiles', label: 'Ceramic Tiles' },
    { value: 'porcelain_tiles', label: 'Porcelain Tiles' },
    { value: 'tiles', label: 'Tiles (General)' },
    { value: 'vinyl_flooring', label: 'Vinyl Flooring' },
    { value: 'flooring', label: 'Flooring (General)' },
    { value: 'natural_stone', label: 'Natural Stone' },
    { value: 'stone', label: 'Stone (General)' }
];

const OCEAN_SOURCE_LABELS = {
    forwarder_rate_card: 'Rate Card',
    historical_quotes: 'Historical',
    market_default: 'Market Default',
    rate_card: 'Rate Card',
    none: 'None'
};

const DRAYAGE_SOURCE_LABELS = {
    customer_rate_card: 'Rate Card',
    distance_based: 'Distance (Auto)',
    default_distance: 'Default',
    geocoding_haversine: 'Google Maps',
    none: 'None'
};

function makeShipment(index) {
    return {
        id: Date.now() + index,
        label: '',
        destinationPort: '',
        deliveryAddress: '',
        containerType: '20GP',
        containerCount: 1,
        isHeavyWeight: false
    };
}

function ShipmentForm({ shipment, index, onChange, onRemove, canRemove }) {
    const handleChange = (field, value) => {
        onChange(index, { ...shipment, [field]: value });
    };

    return (
        <div style={{
            border: '1px solid var(--border-color)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '0.75rem',
            background: index % 2 === 0 ? 'transparent' : 'var(--bg-muted)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '0.9rem' }}>Shipment {index + 1}</strong>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                        type="text"
                        className="filter-input"
                        placeholder="Label (optional)"
                        value={shipment.label}
                        onChange={(e) => handleChange('label', e.target.value)}
                        style={{ width: '160px', fontSize: '0.8rem' }}
                    />
                    {canRemove && (
                        <button
                            type="button"
                            onClick={() => onRemove(index)}
                            style={{
                                background: 'none',
                                border: '1px solid #dc2626',
                                color: '#dc2626',
                                borderRadius: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                            }}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>

            <div className="form-grid">
                <div className="form-group">
                    <label>Destination Port *</label>
                    <select
                        className="filter-input"
                        value={shipment.destinationPort}
                        onChange={(e) => handleChange('destinationPort', e.target.value)}
                    >
                        <option value="">Select port...</option>
                        {US_PORTS.map(p => (
                            <option key={p.code} value={p.code}>{p.label}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Delivery Address</label>
                    <PlaceAutocomplete
                        value={shipment.deliveryAddress}
                        onChange={(val) => handleChange('deliveryAddress', val)}
                        placeholder="Address or zip..."
                        types="geocode"
                    />
                </div>

                <div className="form-group">
                    <label>Container Type</label>
                    <select
                        className="filter-input"
                        value={shipment.containerType}
                        onChange={(e) => handleChange('containerType', e.target.value)}
                    >
                        {CONTAINER_TYPES.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Container Count</label>
                    <input
                        type="number"
                        className="filter-input"
                        min="1"
                        value={shipment.containerCount}
                        onChange={(e) => handleChange('containerCount', e.target.value)}
                    />
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                    <input
                        type="checkbox"
                        id={`heavy-${shipment.id}`}
                        checked={shipment.isHeavyWeight}
                        onChange={(e) => handleChange('isHeavyWeight', e.target.checked)}
                    />
                    <label htmlFor={`heavy-${shipment.id}`} style={{ margin: 0 }}>Heavy</label>
                </div>
            </div>
        </div>
    );
}

function EstimateResult({ result, label }) {
    const est = result?.estimate;
    const locked = result?.lockedPrice;
    const meta = result?.metadata;
    const autoDerived = meta?.autoDerived;

    if (!est) return null;

    return (
        <div className="form-section" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{label}</h3>
                {est.estimateType && (
                    <span className="badge badge-tag" style={{ fontSize: '0.75rem' }}>
                        {est.estimateType === 'landed_cost' ? 'Landed Cost' : 'Shipping Only'}
                    </span>
                )}
            </div>

            {autoDerived && (autoDerived.originCountry || autoDerived.hsCode || autoDerived.distanceMiles) && (
                <div style={{ background: 'var(--bg-muted)', padding: '0.5rem 0.75rem', borderRadius: '0.25rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                    <strong>Auto-derived: </strong>
                    {autoDerived.originCountry && <span>Country: {autoDerived.originCountry} </span>}
                    {autoDerived.hsCode && <span>| HS: {autoDerived.hsCode} </span>}
                    {autoDerived.distanceMiles && <span>| Distance: {autoDerived.distanceMiles} mi </span>}
                </div>
            )}

            <table className="simple-table">
                <thead>
                    <tr>
                        <th>Component</th>
                        <th style={{ textAlign: 'right' }}>Estimate</th>
                        <th style={{ textAlign: 'right' }}>Locked</th>
                        <th>Source</th>
                    </tr>
                </thead>
                <tbody>
                    {est.fobValue != null && (
                        <tr>
                            <td>FOB Value</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(est.fobValue)}</td>
                            <td style={{ textAlign: 'right' }}>-</td>
                            <td><span className="text-muted">Input</span></td>
                        </tr>
                    )}
                    <tr>
                        <td>
                            Ocean Freight
                            {est.forwarderName && <span className="text-muted" style={{ fontSize: '0.75rem' }}> ({est.forwarderName})</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(est.oceanFreight)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(locked.oceanFreight)}</td>
                        <td><span className="badge badge-tag">{OCEAN_SOURCE_LABELS[est.oceanFreightSource] || est.oceanFreightSource}</span></td>
                    </tr>

                    {est.customs && (
                        <>
                            <tr style={{ background: 'var(--bg-muted)' }}>
                                <td colSpan={4}><strong>US Customs</strong></td>
                            </tr>
                            <tr>
                                <td style={{ paddingLeft: '1.5rem' }}>Duty ({formatPercent(est.customs.dutyRatePct)})</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(est.customs.duty)}</td>
                                <td style={{ textAlign: 'right' }}></td>
                                <td></td>
                            </tr>
                            <tr>
                                <td style={{ paddingLeft: '1.5rem' }}>MPF</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(est.customs.mpf)}</td>
                                <td style={{ textAlign: 'right' }}></td>
                                <td></td>
                            </tr>
                            <tr>
                                <td style={{ paddingLeft: '1.5rem' }}>HMF</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(est.customs.hmf)}</td>
                                <td style={{ textAlign: 'right' }}></td>
                                <td></td>
                            </tr>
                            {est.customs.section301 > 0 && (
                                <tr>
                                    <td style={{ paddingLeft: '1.5rem' }}>Section 301</td>
                                    <td style={{ textAlign: 'right' }}>{formatCurrency(est.customs.section301)}</td>
                                    <td style={{ textAlign: 'right' }}></td>
                                    <td></td>
                                </tr>
                            )}
                            <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                                <td style={{ paddingLeft: '1.5rem' }}><strong>Total Customs</strong></td>
                                <td style={{ textAlign: 'right' }}><strong>{formatCurrency(est.customs.totalCustoms)}</strong></td>
                                <td style={{ textAlign: 'right' }}><strong>{formatCurrency(locked.totalCustoms)}</strong></td>
                                <td></td>
                            </tr>
                        </>
                    )}

                    {est.customsNote && (
                        <tr style={{ background: 'var(--bg-muted)' }}>
                            <td colSpan={4} className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                {est.customsNote}
                            </td>
                        </tr>
                    )}

                    <tr>
                        <td>
                            Drayage
                            {est.drayageDistanceMiles && (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}> ({est.drayageDistanceMiles} mi)</span>
                            )}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(est.drayage)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(locked.drayage)}</td>
                        <td><span className="badge badge-tag">{DRAYAGE_SOURCE_LABELS[est.drayageSource] || est.drayageSource || 'None'}</span></td>
                    </tr>

                    {est.shippingCost != null && (
                        <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                            <td><strong>Shipping Cost</strong></td>
                            <td style={{ textAlign: 'right' }}><strong>{formatCurrency(est.shippingCost)}</strong></td>
                            <td style={{ textAlign: 'right' }}><strong>{formatCurrency(locked.shippingCost)}</strong></td>
                            <td><span className="text-muted">Ocean + Drayage</span></td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr style={{ borderTop: '2px solid var(--accent-color)', fontWeight: 'bold', fontSize: '1.05rem' }}>
                        <td>TOTAL {est.estimateType === 'shipping_only' ? '(Shipping)' : '(Landed)'}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(est.totalEstimate)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--accent-color)' }}>{formatCurrency(locked.totalLocked)}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                <span>Confidence: <strong style={{ color: est.confidence >= 0.7 ? '#16a34a' : est.confidence >= 0.5 ? '#ea580c' : '#dc2626' }}>{Math.round(est.confidence * 100)}%</strong></span>
                <span>Range: {formatCurrency(est.estimateLow)} – {formatCurrency(est.estimateHigh)}</span>
                <span>Multiplier: {locked.multiplier}x</span>
                <span>{meta?.containerCount}x {meta?.containerType}</span>
            </div>
        </div>
    );
}

export default function StandaloneEstimator() {
    const [originPort, setOriginPort] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [fobValue, setFobValue] = useState('');
    const [shipments, setShipments] = useState([makeShipment(0)]);
    const [results, setResults] = useState(null);
    const [summary, setSummary] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [error, setError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [advancedFields, setAdvancedFields] = useState({
        hsCode: '', originCountry: '', customerName: '', forwarderName: ''
    });

    const derivedCountry = originPort
        ? ORIGIN_PORTS.find(p => p.code === originPort)?.country
            || (originPort.length >= 4 ? originPort.substring(0, 2) : '')
        : advancedFields.originCountry || '';

    const updateShipment = (index, updated) => {
        setShipments(prev => prev.map((s, i) => i === index ? updated : s));
    };

    const addShipment = () => {
        if (shipments.length < 10) {
            setShipments(prev => [...prev, makeShipment(prev.length)]);
        }
    };

    const removeShipment = (index) => {
        setShipments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setResults(null);
        setSummary(null);

        if (!originPort) {
            setError('Origin port is required.');
            return;
        }

        const invalidShipments = shipments.filter(s => !s.destinationPort);
        if (invalidShipments.length > 0) {
            setError('All shipments need a destination port.');
            return;
        }

        try {
            setCalculating(true);

            const shipmentPayloads = shipments.map((s, i) => {
                const payload = {
                    label: s.label || `Shipment ${i + 1}`,
                    originPort,
                    destinationPort: s.destinationPort,
                    containerType: s.containerType,
                    containerCount: Number(s.containerCount) || 1,
                    isHeavyWeight: s.isHeavyWeight
                };

                if (s.deliveryAddress) payload.deliveryAddress = s.deliveryAddress;
                if (fobValue) payload.fobValue = Number(fobValue);
                if (productCategory) payload.productCategory = productCategory;

                // Advanced overrides
                if (advancedFields.hsCode) payload.hsCode = advancedFields.hsCode;
                if (advancedFields.originCountry) payload.originCountry = advancedFields.originCountry;
                if (advancedFields.customerName) payload.customerName = advancedFields.customerName;
                if (advancedFields.forwarderName) payload.forwarderName = advancedFields.forwarderName;

                return payload;
            });

            if (shipmentPayloads.length === 1) {
                // Single shipment — use single endpoint
                const response = await estimateAPI.calculateStandalone(shipmentPayloads[0]);
                setResults([{ label: shipmentPayloads[0].label, success: true, ...response.data }]);
                setSummary(null);
            } else {
                // Multi-shipment — use batch endpoint
                const response = await estimateAPI.calculateBatch(shipmentPayloads);
                setResults(response.data.shipments);
                setSummary(response.data.summary);
            }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Estimate calculation failed';
            setError(msg);
        } finally {
            setCalculating(false);
        }
    };

    const handleReset = () => {
        setOriginPort('');
        setProductCategory('');
        setFobValue('');
        setShipments([makeShipment(0)]);
        setResults(null);
        setSummary(null);
        setError(null);
        setShowAdvanced(false);
        setAdvancedFields({ hsCode: '', originCountry: '', customerName: '', forwarderName: '' });
    };

    return (
        <div className="admin-page">
            <div className="page-header">
                <h1>Freight Estimate Calculator</h1>
                <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
            </div>

            <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
                Calculate freight estimates for one or more shipments. FOB value is optional — without it, you get shipping cost only (ocean + drayage). With FOB, you get the full landed cost including customs.
            </p>

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
            )}

            <form onSubmit={handleSubmit}>
                {/* Shared fields (apply to all shipments) */}
                <div className="form-section">
                    <h2>Order Details</h2>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Origin Port *</label>
                            <select
                                className="filter-input"
                                value={originPort}
                                onChange={(e) => setOriginPort(e.target.value)}
                            >
                                <option value="">Select port...</option>
                                {ORIGIN_PORTS.map(p => (
                                    <option key={p.code} value={p.code}>{p.label}</option>
                                ))}
                            </select>
                            {derivedCountry && (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>Origin country: {derivedCountry}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Product Category</label>
                            <select
                                className="filter-input"
                                value={productCategory}
                                onChange={(e) => setProductCategory(e.target.value)}
                            >
                                {PRODUCT_CATEGORIES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                Auto-resolves HS code + duties (requires FOB)
                            </span>
                        </div>

                        <div className="form-group">
                            <label>FOB Value (USD)</label>
                            <input
                                type="number"
                                className="filter-input"
                                min="0"
                                step="0.01"
                                placeholder="Optional — for landed cost"
                                value={fobValue}
                                onChange={(e) => setFobValue(e.target.value)}
                            />
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {fobValue ? 'Landed cost: FOB + shipping + customs' : 'Without FOB: shipping cost only (ocean + drayage)'}
                            </span>
                        </div>
                    </div>

                    {/* Advanced */}
                    <div style={{ marginTop: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '0.8rem', padding: 0, textDecoration: 'underline' }}
                        >
                            {showAdvanced ? 'Hide' : 'Show'} advanced options
                        </button>
                    </div>

                    {showAdvanced && (
                        <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                            <div className="form-group">
                                <label>HS Code (override)</label>
                                <input type="text" className="filter-input" placeholder="e.g. 6907.21" value={advancedFields.hsCode} onChange={(e) => setAdvancedFields(p => ({ ...p, hsCode: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label>Origin Country (override)</label>
                                <input type="text" className="filter-input" placeholder="e.g. CN" maxLength={10} value={advancedFields.originCountry} onChange={(e) => setAdvancedFields(p => ({ ...p, originCountry: e.target.value.toUpperCase() }))} />
                            </div>
                            <div className="form-group">
                                <label>Customer Name</label>
                                <input type="text" className="filter-input" placeholder="For drayage rate card" value={advancedFields.customerName} onChange={(e) => setAdvancedFields(p => ({ ...p, customerName: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label>Forwarder Name</label>
                                <input type="text" className="filter-input" placeholder="For ocean rate + broker fees" value={advancedFields.forwarderName} onChange={(e) => setAdvancedFields(p => ({ ...p, forwarderName: e.target.value }))} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Shipment legs */}
                <div className="form-section" style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2>Shipments ({shipments.length})</h2>
                        {shipments.length < 10 && (
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={addShipment}
                                style={{ fontSize: '0.85rem' }}
                            >
                                + Add Shipment
                            </button>
                        )}
                    </div>

                    {shipments.map((s, i) => (
                        <ShipmentForm
                            key={s.id}
                            shipment={s}
                            index={i}
                            onChange={updateShipment}
                            onRemove={removeShipment}
                            canRemove={shipments.length > 1}
                        />
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={calculating}>
                        {calculating ? 'Calculating...' : `Calculate ${shipments.length > 1 ? `${shipments.length} Estimates` : 'Estimate'}`}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleReset}>Reset</button>
                </div>
            </form>

            {/* Results */}
            {results && (
                <div style={{ marginTop: '1.5rem' }}>
                    {/* Per-shipment results */}
                    {results.map((r, i) => (
                        r.success ? (
                            <EstimateResult key={i} result={r} label={r.label || `Shipment ${i + 1}`} />
                        ) : (
                            <div key={i} className="form-section alert alert-error" style={{ marginBottom: '1rem' }}>
                                <strong>{r.label || `Shipment ${i + 1}`}</strong>: {r.error}
                            </div>
                        )
                    ))}

                    {/* Combined summary for multi-shipment */}
                    {summary && (
                        <div className="form-section" style={{ background: 'var(--accent-color)', color: '#fff', padding: '1rem' }}>
                            <h2 style={{ margin: '0 0 0.75rem', color: '#fff' }}>Combined Summary</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Shipments</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{summary.successCount}/{summary.shipmentCount}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Total Containers</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{summary.totalContainers}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Shipping Cost</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{formatCurrency(summary.totalShippingCost)}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Total Estimate</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{formatCurrency(summary.totalEstimate)}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Total Locked Price</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{formatCurrency(summary.totalLocked)}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
